import React, { useCallback, useMemo, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  Typography, 
  Alert, 
  Skeleton, 
  Slide, 
  useMediaQuery,
  Box,
  Chip,
  Stack,
  Avatar,
  AvatarGroup,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import GroupIcon from '@mui/icons-material/Group';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import API from '../../API';
import ChatScreen from '../../Components/ChatScreen';
import useIsDesktop from '../../Hooks/useIsDesktop';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const OrganizerChatModal = ({ open, onClose, organizer }) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  // Get conversation info
  const { data: conversationData, isLoading: conversationLoading } = useQuery({
    queryKey: ['organizer-conversation', organizer?.id],
    queryFn: () => API.files.organizers.getConversation(organizer.id),
    enabled: !!open && !!organizer?.id,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Get participants info
  const { data: participantsData, isLoading: participantsLoading } = useQuery({
    queryKey: ['organizer-participants', organizer?.id],
    queryFn: () => API.files.organizers.getConversationParticipants(organizer.id),
    enabled: !!open && !!organizer?.id,
    refetchOnWindowFocus: false,
  });

  // Create conversation mutation
  const createConversationMutation = useMutation({
    mutationFn: () => API.files.organizers.createConversation(organizer.id),
    onSuccess: () => {
      queryClient.invalidateQueries(['organizer-conversation', organizer.id]);
    },
    onError: (error) => {
      console.error('Failed to create conversation:', error);
    }
  });

  const handleClose = useCallback(
    (event, reason) => {
      if (reason === 'backdropClick' && isMobile) return;
      onClose();
    },
    [onClose, isMobile]
  );

  const handleCreateConversation = useCallback(() => {
    createConversationMutation.mutate();
  }, [createConversationMutation]);

  const modalStyles = useMemo(
  () => ({
    paper: {
      margin: isMobile ? 0 : theme.spacing(3),
      width: isMobile ? '100vw' : 'auto',
      maxWidth: isMobile ? '100vw' : '85vw', // Increased from 70vw to 85vw
      minWidth: isMobile ? '100vw' : 720, // Increased from 520 to 720
      maxHeight: isMobile ? '100vh' : '92vh', // Increased from 90vh to 92vh
      height: isMobile ? '100vh' : 'auto',
      borderRadius: isMobile ? 0 : 4,
      overflow: 'hidden',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(22,22,24,0.85)'
          : 'rgba(255,255,255,0.9)',
      backdropFilter: 'saturate(140%) blur(10px)',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.35)}`,
      display: 'flex',
      flexDirection: 'column',
    },
    dialogTitle: {
      px: 3,
      py: 2,
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexShrink: 0,
      minHeight: 80,
      position: 'sticky',
      top: 0,
      zIndex: 2,
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(18,18,18,0.7)'
          : 'rgba(255,255,255,0.7)',
      backdropFilter: 'blur(8px)',
    },
    dialogContent: {
      p: 0,
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    chatContainer: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
  }),
  [theme, isMobile]
);
  // Show participants preview in title
  const ParticipantChips = ({ participants, maxVisible = 3 }) => {
    if (!participants?.length) return null;

    const visibleParticipants = participants.slice(0, maxVisible);
    const remainingCount = Math.max(0, participants.length - maxVisible);

    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
        <AvatarGroup max={maxVisible + 1} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
          {visibleParticipants.map((participant, index) => (
            <Avatar
              key={participant.id}
              src={participant.avatar}
              alt={participant.name}
              sx={{ 
                bgcolor: participant.is_owner ? 'primary.main' : 'secondary.main',
                border: participant.is_owner ? `2px solid ${theme.palette.primary.main}` : 'none'
              }}
            >
              {participant.name?.charAt(0)?.toUpperCase()}
            </Avatar>
          ))}
        </AvatarGroup>
        <Typography variant="caption" color="text.secondary">
          {participants.length} participant{participants.length !== 1 ? 's' : ''}
        </Typography>
      </Stack>
    );
  };

  const TitleContent = (
    <Box sx={{ flex: 1, mr: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <ChatIcon color="primary" />
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          {organizer?.name}
        </Typography>
        <Chip 
          label="Team Chat" 
          color="primary" 
          size="small" 
          variant="outlined"
          sx={{ ml: 1 }}
        />
      </Box>
      
      {!participantsLoading && (
        <ParticipantChips participants={participantsData?.participants} />
      )}
      
      {participantsLoading && (
        <Skeleton variant="rectangular" width={200} height={32} sx={{ mt: 1 }} />
      )}
    </Box>
  );

  // Handle different states
  if (conversationLoading || participantsLoading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={!isDesktop}
        TransitionComponent={Transition}
        transitionDuration={280}
        PaperProps={{ sx: modalStyles.paper }}
        BackdropProps={{ sx: { backdropFilter: 'blur(2px)' } }}
      >
        <DialogTitle sx={modalStyles.dialogTitle}>
          <Skeleton variant="text" width="100%" height={60} />
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={modalStyles.dialogContent}>
          <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={400} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // No conversation exists yet
  if (!conversationData?.conversation_id) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={!isDesktop}
        TransitionComponent={Transition}
        transitionDuration={280}
        PaperProps={{ sx: modalStyles.paper }}
        BackdropProps={{ sx: { backdropFilter: 'blur(2px)' } }}
      >
        <DialogTitle sx={modalStyles.dialogTitle}>
          {TitleContent}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={modalStyles.dialogContent}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            p: 4,
            textAlign: 'center'
          }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Start Team Conversation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
              Create a group chat for everyone who has access to "{organizer?.name}". 
              This includes the owner and all users it's shared with.
            </Typography>
            
            {participantsData?.participants && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Chat will include:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                  {participantsData.participants.map((participant) => (
                    <Chip
                      key={participant.id}
                      avatar={<Avatar src={participant.avatar}>{participant.name?.charAt(0)}</Avatar>}
                      label={participant.name}
                      variant={participant.is_owner ? "filled" : "outlined"}
                      color={participant.is_owner ? "primary" : "default"}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {participantsData?.total_count < 2 && (
              <Alert severity="info" sx={{ mb: 2, maxWidth: 400 }}>
                You need to share this organizer with other users before creating a team chat.
              </Alert>
            )}

            <Button
              variant="contained"
              startIcon={<ChatIcon />}
              onClick={handleCreateConversation}
              disabled={createConversationMutation.isLoading || participantsData?.total_count < 2}
              size="large"
            >
              {createConversationMutation.isLoading ? 'Creating...' : 'Start Team Chat'}
            </Button>

            {createConversationMutation.error && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 400 }}>
                {createConversationMutation.error?.response?.data?.error || 'Failed to create conversation'}
              </Alert>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Conversation exists - show chat
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={!isDesktop}
      TransitionComponent={Transition}
      transitionDuration={280}
      PaperProps={{ sx: modalStyles.paper }}
      BackdropProps={{ sx: { backdropFilter: 'blur(2px)' } }}
      disableEscapeKeyDown={isMobile}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        {TitleContent}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={modalStyles.dialogContent}>
        <Box sx={modalStyles.chatContainer}>
          <ChatScreen
            showWarning={false}
            conversationId={conversationData.conversation_id}
            sx={{
              height: '100%',
              minHeight: isMobile ? '100%' : 520,
              flex: 1,
              pb: isMobile ? 'env(safe-area-inset-bottom)' : 0,
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizerChatModal;